import React, { useState, useEffect } from 'react';
import { Button, Box, Typography, LinearProgress, Alert, FormControl, InputLabel, Select, MenuItem, FormControlLabel, RadioGroup, Radio, Checkbox } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import axios from 'axios';

const UploadForm = ({ onResults }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('llama3.2:latest');
  const [contentType, setContentType] = useState('generic');
  const [summaryType, setSummaryType] = useState('general');
  const [whisperModel, setWhisperModel] = useState('small');
  const [audioLanguage, setAudioLanguage] = useState('es');
  const [diarize, setDiarize] = useState(true);
  const [numSpeakers, setNumSpeakers] = useState('auto');
  const [processingMode, setProcessingMode] = useState('now'); // 'now' or 'queue'
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  const whisperModels = [
    { value: 'tiny', label: 'Tiny (39MB) - Muy rápido' },
    { value: 'base', label: 'Base (140MB) - Rápido' },
    { value: 'small', label: 'Small (466MB) - Balance' },
    { value: 'medium', label: 'Medium (1.5GB) - Preciso' },
    { value: 'large', label: 'Large (2.9GB) - Más preciso' }
  ];

  const languages = [
    { value: 'es', label: '🇪🇸 Español' },
    { value: 'ca', label: '🇪🇸 Català' },
    { value: 'en', label: '🇬🇧 English' },
    { value: 'fr', label: '🇫🇷 Français' },
    { value: 'de', label: '🇩🇪 Deutsch' },
    { value: 'it', label: '🇮🇹 Italiano' },
    { value: 'pt', label: '🇵🇹 Português' },
    { value: 'pl', label: '🇵🇱 Polski' },
    { value: 'tr', label: '🇹🇷 Türkçe' },
    { value: 'ru', label: '🇷🇺 Русский' },
    { value: 'zh', label: '🇨🇳 中文' },
    { value: 'ja', label: '🇯🇵 日本語' },
    { value: 'ko', label: '🇰🇷 한국어' }
  ];

  useEffect(() => {
    const fetchModels = async () => {
      try {
        // Use relative URL - NGINX will proxy to backend
        const response = await axios.get('/api/models');
        setModels(response.data.models);
      } catch (err) {
        console.error('Error fetching models:', err);
      }
    };
    fetchModels();
  }, []);

  // Polling effect for job status
  useEffect(() => {
    if (jobId && jobStatus?.status === 'processing' && !pollingInterval) {
      const pollJobStatus = async () => {
        try {
          const response = await axios.get(`/api/job/${jobId}`);
          const status = response.data;
          setJobStatus(status);

          if (status.status === 'completed' && status.results) {
            onResults(status.results);
            setJobId(null);
            setJobStatus(null);
            if (pollingInterval) {
              clearInterval(pollingInterval);
              setPollingInterval(null);
            }
          } else if (status.status === 'failed') {
            setError(status.error || 'Procesamiento fallido');
            setJobId(null);
            setJobStatus(null);
            if (pollingInterval) {
              clearInterval(pollingInterval);
              setPollingInterval(null);
            }
          } else {
            // Update progress and message based on current status
            let userFriendlyMessage = status.message;
            const isDiarizing = /hablantes|diariz|separando/i.test(status.message || '');
            if (!isDiarizing) {
              if (status.progress >= 0 && status.progress < 20) {
                userFriendlyMessage = 'Validando archivos de audio...';
              } else if (status.progress >= 20 && status.progress < 80) {
                userFriendlyMessage = 'Transcribiendo audio con IA...';
              } else if (status.progress >= 80 && status.progress < 100) {
                userFriendlyMessage = 'Generando resumen inteligente...';
              } else if (status.progress >= 100) {
                userFriendlyMessage = 'Finalizando procesamiento...';
              }
            }

            setJobStatus({
              ...status,
              message: userFriendlyMessage
            });
          }
        } catch (err) {
          console.error('Error polling job status:', err);
        }
      };

      // Start polling every 3 seconds
      const interval = setInterval(pollJobStatus, 3000);
      setPollingInterval(interval);

      return () => {
        clearInterval(interval);
        setPollingInterval(null);
      };
    }
  }, [jobId, jobStatus?.status, onResults]);

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const validFiles = selectedFiles.filter(file =>
      file.type === 'audio/mpeg' ||
      file.type === 'audio/mp4' ||
      file.name.toLowerCase().endsWith('.m4a')
    );
    if (validFiles.length !== selectedFiles.length) {
      setError('Solo se permiten archivos MP3 y M4A');
    } else {
      setError('');
      setFiles(validFiles);
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setError('');
    setJobId(null);
    setJobStatus(null);

    try {
      if (processingMode === 'queue') {
        // Upload files in batches to avoid nginx 413 errors
        const batchSize = 1; // Upload 1 file at a time for maximum reliability with large files
        const allUploadedFiles = [];
        const totalFiles = files.length;

        for (let i = 0; i < files.length; i += batchSize) {
          const batch = files.slice(i, i + batchSize);
          const batchFormData = new FormData();
          batch.forEach(file => batchFormData.append('files', file));

          const batchStart = i + 1;
          const batchEnd = Math.min(i + batchSize, totalFiles);

          setJobStatus({
            status: 'processing',
            progress: Math.round((i / totalFiles) * 50), // First 50% for upload
            message: `Subiendo archivos ${batchStart}-${batchEnd} de ${totalFiles}...`
          });

          try {
            const uploadResponse = await axios.post('/api/upload-temp', batchFormData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 600000, // 10 minutes timeout for large files
            });

            allUploadedFiles.push(...uploadResponse.data.files);
          } catch (batchError) {
            console.error(`Error uploading batch ${batchStart}-${batchEnd}:`, batchError);

            // Provide more specific error message
            let errorMessage = `Error al subir archivos ${batchStart}-${batchEnd}`;
            if (batchError.response?.status === 413) {
              errorMessage += ': Archivos demasiado grandes. Intente con menos archivos o archivos más pequeños.';
            } else if (batchError.response?.status === 504) {
              errorMessage += ': Timeout del servidor. Los archivos pueden ser demasiado grandes.';
            } else if (batchError.message?.includes('timeout')) {
              errorMessage += ': Timeout de conexión. Los archivos pueden ser demasiado grandes.';
            } else {
              errorMessage += `: ${batchError.response?.data?.detail || batchError.message}`;
            }

            setError(errorMessage);
            setUploading(false);
            return; // Stop processing further batches
          }
        }

        // Then add to queue with actual server paths
        const queueData = allUploadedFiles.map((uploadedFile, index) => ({
          filename: files[index].name,
          file_path: uploadedFile.path,
          file_size: files[index].size,
          model: selectedModel,
          content_type: contentType,
          summary_type: summaryType,
          whisper_model: whisperModel,
          language: audioLanguage,
          diarize: diarize,
          num_speakers: numSpeakers === 'auto' ? null : Number(numSpeakers)
        }));

        setJobStatus({
          status: 'processing',
          progress: 75,
          message: 'Agregando archivos a la cola...'
        });

        const queueResponse = await axios.post('/api/queue/add', queueData, {
          headers: { 'Content-Type': 'application/json' },
        });

        setJobStatus({
          status: 'completed',
          progress: 100,
          message: '¡Archivos agregados exitosamente!'
        });

        setError('');
        alert(`✅ ${queueResponse.data.added} archivo(s) agregado(s) a la cola. Cola actual: ${queueResponse.data.queue_length} elemento(s)`);
        setFiles([]); // Clear files
        setUploading(false);

      } else {
        // Process immediately
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));

        const uploadParams = new URLSearchParams({
          model: selectedModel,
          content_type: contentType,
          summary_type: summaryType,
          whisper_model: whisperModel,
          language: audioLanguage,
          diarize: diarize ? 'true' : 'false'
        });
        if (diarize && numSpeakers !== 'auto') {
          uploadParams.set('num_speakers', numSpeakers);
        }
        const apiUrl = `/api/upload?${uploadParams.toString()}`;
        const response = await axios.post(apiUrl, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        // Set job ID and start polling
        setJobId(response.data.job_id);
        setJobStatus({
          status: 'processing',
          progress: 0,
          message: 'Iniciando procesamiento de archivos...'
        });
      }

    } catch (err) {
      setError(err.response?.data?.detail || 'Error al procesar los archivos');
      setUploading(false);
    }
  };

  return (
    <Box sx={{ p: 4, mb: 4 }} className="upload-card">
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#333' }}>
        📁 Subir Archivos de Audio
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'rgba(0,0,0,0.6)' }}>
        Selecciona archivos MP3 o M4A para transcribir con IA avanzada
      </Typography>
      <input
        type="file"
        multiple
        accept=".mp3,.m4a"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        id="file-input"
      />
      <label htmlFor="file-input">
        <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />}>
          Seleccionar Archivos MP3/M4A
        </Button>
      </label>
      {files.length > 0 && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          {files.length} archivo(s) seleccionado(s)
        </Typography>
      )}

      {/* Processing Mode Selection */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem' }}>
          🎯 Modo de Procesamiento
        </Typography>
        <FormControl component="fieldset">
          <RadioGroup
            row
            value={processingMode}
            onChange={(e) => setProcessingMode(e.target.value)}
          >
            <FormControlLabel
              value="now"
              control={<Radio />}
              label="Procesar Ahora"
              sx={{ mr: 4 }}
            />
            <FormControlLabel
              value="queue"
              control={<Radio />}
              label="Agregar a Cola"
            />
          </RadioGroup>
        </FormControl>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {processingMode === 'now'
            ? 'Los archivos se procesarán inmediatamente'
            : 'Los archivos se agregarán a la cola para procesamiento posterior'
          }
        </Typography>
      </Box>

      <FormControl fullWidth sx={{ mt: 3 }} className="select-field">
        <InputLabel>Modelo Whisper</InputLabel>
        <Select
          value={whisperModel}
          label="Modelo Whisper"
          onChange={(e) => setWhisperModel(e.target.value)}
        >
          {whisperModels.map((m) => (
            <MenuItem key={m.value} value={m.value}>
              {m.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mt: 3 }} className="select-field">
        <InputLabel>Idioma del Audio</InputLabel>
        <Select
          value={audioLanguage}
          label="Idioma del Audio"
          onChange={(e) => setAudioLanguage(e.target.value)}
        >
          {languages.map((lang) => (
            <MenuItem key={lang.value} value={lang.value}>
              {lang.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={diarize}
              onChange={(e) => setDiarize(e.target.checked)}
            />
          }
          label="Separar hablantes (diarización)"
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Añade etiquetas de hablante a cada intervención. Requiere el servicio de diarización de Noosphere.
        </Typography>
        <FormControl fullWidth className="select-field" disabled={!diarize}>
          <InputLabel>Número de hablantes</InputLabel>
          <Select
            value={numSpeakers}
            label="Número de hablantes"
            onChange={(e) => setNumSpeakers(e.target.value)}
          >
            <MenuItem value="auto">Automático</MenuItem>
            <MenuItem value="2">2 hablantes</MenuItem>
            <MenuItem value="3">3 hablantes</MenuItem>
            <MenuItem value="4">4 hablantes</MenuItem>
            <MenuItem value="5">5 hablantes</MenuItem>
            <MenuItem value="6">6 hablantes</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <FormControl fullWidth sx={{ mt: 3 }} className="select-field">
        <InputLabel>Tipo de contenido</InputLabel>
        <Select
          value={contentType}
          label="Tipo de contenido"
          onChange={(e) => {
            setContentType(e.target.value);
            // Reset summary type when content type changes
            if (e.target.value === 'meeting') setSummaryType('general');
            else if (e.target.value === 'talk') setSummaryType('general');
            else setSummaryType('general');
          }}
        >
          <MenuItem value="generic">Audio Genérico</MenuItem>
          <MenuItem value="meeting">Reunión</MenuItem>
          <MenuItem value="talk">Charla/Conferencia</MenuItem>
        </Select>
      </FormControl>

      {contentType === 'meeting' && (
        <FormControl fullWidth sx={{ mt: 3 }} className="select-field">
          <InputLabel>Tipo de resumen</InputLabel>
          <Select
            value={summaryType}
            label="Tipo de resumen"
            onChange={(e) => setSummaryType(e.target.value)}
          >
            <MenuItem value="general">Resumen General</MenuItem>
            <MenuItem value="minutes">Acta de Reunión</MenuItem>
          </Select>
        </FormControl>
      )}

      {contentType === 'talk' && (
        <FormControl fullWidth sx={{ mt: 3 }} className="select-field">
          <InputLabel>Tipo de resumen</InputLabel>
          <Select
            value={summaryType}
            label="Tipo de resumen"
            onChange={(e) => setSummaryType(e.target.value)}
          >
            <MenuItem value="general">Resumen General</MenuItem>
          </Select>
        </FormControl>
      )}

      {contentType === 'generic' && (
        <FormControl fullWidth sx={{ mt: 3 }} className="select-field">
          <InputLabel>Resumen</InputLabel>
          <Select
            value={summaryType}
            label="Resumen"
            onChange={(e) => setSummaryType(e.target.value)}
          >
            <MenuItem value="general">Generar Resumen</MenuItem>
            <MenuItem value="none">Sin Resumen</MenuItem>
          </Select>
        </FormControl>
      )}

      {models.length > 0 && summaryType !== 'none' && (
        <FormControl fullWidth sx={{ mt: 3 }} className="select-field">
          <InputLabel>Modelo para resumen</InputLabel>
          <Select
            value={selectedModel}
            label="Modelo para resumen"
            onChange={(e) => setSelectedModel(e.target.value)}
          >
            {models.map((model) => (
              <MenuItem key={model} value={model}>
                {model}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Button
        variant="contained"
        onClick={handleUpload}
        disabled={files.length === 0 || uploading || jobStatus?.status === 'processing'}
        sx={{ mt: 3, mb: 2 }}
        className="upload-button"
      >
        {jobStatus?.status === 'processing' ? '⏳ Procesando...' :
         uploading ? '📤 Subiendo archivos...' :
         processingMode === 'queue' ? '➕ Agregar a Cola' :
         '🚀 Subir y Transcribir'}
      </Button>

      {jobStatus?.status === 'processing' && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {jobStatus.message}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={jobStatus.progress}
            sx={{ height: 8, borderRadius: 4 }}
            className="progress-bar"
          />
          <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
            {jobStatus.progress}% completado
          </Typography>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </Box>
  );
};

export default UploadForm;
